import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Calendar, FileText, Loader2 } from "lucide-react";
import QuotesList from "./admin/QuotesList";
import ContractsList from "./admin/ContractsList";
import ReviewsPanel from "./admin/ReviewsPanel";
import ChangePassword from "./admin/ChangePassword";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "proexec_admin_token";

export default function AdminDashboard() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [email, setEmail] = useState("ExecProVirtualSolutions@gmx.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [sending, setSending] = useState("");

  const authHeader = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

  useEffect(() => {
    if (!token) {
      setAuthed(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, authHeader(token))
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
      });
  }, [token]);

  useEffect(() => {
    if (authed !== true) return;
    Promise.all([
      axios.get(`${API}/admin/bookings`, authHeader(token)),
      axios.get(`${API}/admin/inquiries`, authHeader(token)),
      axios.get(`${API}/admin/quotes`, authHeader(token)),
      axios.get(`${API}/admin/contracts`, authHeader(token)),
    ])
      .then(([b, i, q, c]) => {
        setBookings(b.data);
        setInquiries(i.data);
        setQuotes(q.data);
        setContracts(c.data);
      })
      .catch(() => toast.error("Could not load data."));
  }, [authed, token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  const sendContract = async (name, mail) => {
    setSending(mail);
    try {
      const { data } = await axios.post(`${API}/admin/send-contract`, { name, email: mail }, authHeader(token));
      setContracts((prev) => [
        { id: data.id, name, email: mail, status: "sent", created_at: new Date().toISOString() },
        ...prev,
      ]);
      toast.success(`Contract sent to ${mail}`);
    } catch {
      toast.error("Could not send contract.");
    } finally {
      setSending("");
    }
  };

  if (authed === null) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ backgroundColor: "var(--bg-primary)" }}>
        <Loader2 className="animate-spin" color="var(--rose-mocha)" />
      </div>
    );
  }

  if (authed === false) {
    return (
      <div
        className="min-h-screen grid place-items-center px-6 font-body"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl p-8 shadow-xl"
          style={{ backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-light)" }}
        >
          <span
            className="grid place-items-center w-12 h-12 rounded-full mb-5"
            style={{ backgroundColor: "var(--rose-mocha)" }}
          >
            <Lock size={20} color="#FBF6F3" />
          </span>
          <h1 className="font-serif text-2xl mb-1" style={{ color: "var(--text-primary)" }}>
            Leads Dashboard
          </h1>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
            Please sign in to continue.
          </p>
          <label className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--rose-mocha-deep)" }}>
            Email
          </label>
          <input
            data-testid="admin-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent py-2.5 mb-4 outline-none text-base"
            style={{ borderBottom: "1px solid var(--border-light)", color: "var(--text-primary)" }}
          />
          <label className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--rose-mocha-deep)" }}>
            Password
          </label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent py-2.5 mb-7 outline-none text-base"
            style={{ borderBottom: "1px solid var(--border-light)", color: "var(--text-primary)" }}
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full rounded-full py-3.5 text-sm font-medium disabled:opacity-60"
            style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-body" style={{ backgroundColor: "var(--bg-primary)" }}>
      <header
        className="px-6 md:px-10 py-6 flex items-center justify-between"
        style={{ borderBottom: "1px solid var(--border-light)" }}
      >
        <h1 className="font-serif text-2xl" style={{ color: "var(--text-primary)" }}>
          ProExec<span style={{ color: "var(--accent-gold)" }}>.</span>{" "}
          <span className="text-base font-body" style={{ color: "var(--text-secondary)" }}>
            Leads
          </span>
        </h1>
        <div className="flex items-center gap-6">
        <ChangePassword
          token={token}
          onChanged={(t) => {
            localStorage.setItem(TOKEN_KEY, t);
            setToken(t);
          }}
        />
        <button
          onClick={logout}
          data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 text-sm"
          style={{ color: "var(--rose-mocha-deep)" }}
        >
          <LogOut size={16} /> Sign out
        </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1000px] px-6 md:px-10 py-10">
        <div className="flex flex-wrap gap-3 mb-8">
          {[
            { key: "bookings", label: `Bookings (${bookings.length})` },
            { key: "inquiries", label: `Inquiries (${inquiries.length})` },
            { key: "quotes", label: `Quotes (${quotes.length})` },
            { key: "contracts", label: `Contracts (${contracts.length})` },
            { key: "reviews", label: "Reviews" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="rounded-full px-5 py-2 text-sm transition-colors"
              style={{
                backgroundColor: tab === t.key ? "var(--mocha-brown)" : "transparent",
                color: tab === t.key ? "#FBF6F3" : "var(--text-secondary)",
                border: "1px solid var(--border-light)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div data-testid="admin-bookings-list" className="flex flex-col gap-4">
            {bookings.length === 0 && (
              <p style={{ color: "var(--text-secondary)" }}>No bookings yet.</p>
            )}
            {bookings.map((b) => (
              <div
                key={b.id}
                className="rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                style={{ backgroundColor: "#fff", border: "1px solid var(--border-light)" }}
              >
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar size={15} color="var(--rose-mocha)" />
                    <span className="font-medium" style={{ color: "var(--text-primary)" }}>
                      {b.slot_label}
                    </span>
                    {b.status === "cancelled" && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: "#F3DAD5", color: "var(--rose-mocha-deep)" }}
                      >
                        cancelled
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    {b.name} · {b.email}
                  </p>
                  {b.notes && (
                    <p className="text-sm mt-1 italic" style={{ color: "var(--text-secondary)" }}>
                      "{b.notes}"
                    </p>
                  )}
                </div>
                <button
                  data-testid={`admin-send-contract-${b.id}`}
                  onClick={() => sendContract(b.name, b.email)}
                  disabled={sending === b.email}
                  className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm disabled:opacity-60"
                  style={{ backgroundColor: "var(--accent-gold)", color: "#2E241D" }}
                >
                  <FileText size={15} /> {sending === b.email ? "Sending…" : "Send Contract"}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "contracts" && <ContractsList contracts={contracts} token={token} />}

        {tab === "reviews" && <ReviewsPanel token={token} />}

        {tab === "quotes" && (
          <QuotesList quotes={quotes} sending={sending} onSendContract={sendContract} />
        )}

        {tab === "inquiries" && (
          <div data-testid="admin-inquiries-list" className="flex flex-col gap-4">
            {inquiries.length === 0 && (
              <p style={{ color: "var(--text-secondary)" }}>No inquiries yet.</p>
            )}
            {inquiries.map((q) => (
              <div
                key={q.id}
                className="rounded-2xl p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
                style={{ backgroundColor: "#fff", border: "1px solid var(--border-light)" }}
              >
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Mail size={15} color="var(--rose-mocha)" />
                    <span className="font-medium" style={{ color: "var(--text-primary)" }}>
                      {q.name}
                    </span>
                    <span className="text-sm" style={{ color: "var(--text-secondary)" }}>
                      · {q.email}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    {q.message}
                  </p>
                </div>
                <button
                  data-testid={`admin-send-contract-inq-${q.id}`}
                  onClick={() => sendContract(q.name, q.email)}
                  disabled={sending === q.email}
                  className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm disabled:opacity-60"
                  style={{ backgroundColor: "var(--accent-gold)", color: "#2E241D" }}
                >
                  <FileText size={15} /> {sending === q.email ? "Sending…" : "Send Contract"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
